"use client";

import React, { useState } from "react";
import type { Combo } from "@/src/types/tenant";
import { useCart } from "@/src/contexts/CartContext";

interface BundleAndSaveProps {
  combos?: Combo[];
  outletId?: string;
}

const TAG_STYLES: Record<number, { label: string; color: string }> = {
  0: { label: "Most Popular", color: "var(--chai-tea)" },
  1: { label: "Best Value",   color: "var(--chutney-green)" },
  2: { label: "Gifting",      color: "var(--rising-sun)" },
};

export const BundleAndSave: React.FC<BundleAndSaveProps> = ({
  combos = [],
  outletId = "",
}) => {
  const { addItem } = useCart();
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);

  if (combos.length === 0) return null;

  const visibleCombos = showAll ? combos : combos.slice(0, 3);

  const handleAdd = (combo: Combo) => {
    addItem({
      productId: `combo-${combo.id}`,
      outletId,
      name: combo.name,
      price: combo.sellingPrice,
      quantity: 1,
      image: combo.thumbnail || undefined,
    });
    setAddedIds((prev) => new Set(prev).add(String(combo.id)));
    setTimeout(
      () =>
        setAddedIds((prev) => {
          const next = new Set(prev);
          next.delete(String(combo.id));
          return next;
        }),
      2000
    );
  };

  return (
    <section className="mt-[clamp(40px,5vw,64px)]">
      {/* Section head */}
      <div className="mb-[clamp(16px,2vw,24px)] flex items-end justify-between gap-4">
        <div>
          <h2
            className="font-bold leading-tight"
            style={{ fontSize: "clamp(18px,2.5vw,24px)", letterSpacing: "-0.5px" }}
          >
            Bundle &amp; save
          </h2>
          <p className="mt-1 text-sm" style={{ color: "var(--techno-black-50)" }}>
            Curated combos hand-picked by the maker.
          </p>
        </div>
        {combos.length > 3 && (
          <button
            onClick={() => setShowAll((v) => !v)}
            className="shrink-0 text-sm font-semibold transition"
            style={{ color: "var(--rising-sun)" }}
          >
            {showAll ? "Show less" : `View all (${combos.length})`}
          </button>
        )}
      </div>

      {/* Bundle grid */}
      <div className="grid grid-cols-1 gap-[clamp(14px,2vw,20px)] md:grid-cols-2 lg:grid-cols-3">
        {visibleCombos.map((combo, index) => {
          const discountPct =
            combo.mrp > 0
              ? Math.round(((combo.mrp - combo.sellingPrice) / combo.mrp) * 100)
              : 0;
          const tagStyle = TAG_STYLES[index % 3];
          const isAdded = addedIds.has(String(combo.id));

          return (
            <div
              key={combo.id}
              className="relative flex flex-col gap-4 overflow-hidden rounded-2xl p-[22px] transition-all"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border-soft)",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = "translateY(-3px)";
                e.currentTarget.style.boxShadow = "0 12px 32px rgba(40,30,20,0.08)";
                e.currentTarget.style.borderColor = "var(--border)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = "";
                e.currentTarget.style.boxShadow = "";
                e.currentTarget.style.borderColor = "var(--border-soft)";
              }}
            >
              {/* Tag */}
              <span
                className="self-start rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.6px] text-white"
                style={{ background: tagStyle.color }}
              >
                {combo.tag || tagStyle.label}
              </span>

              {/* Visual */}
              <div
                className="flex h-[110px] items-center justify-center gap-1.5 overflow-hidden rounded-xl"
                style={{ background: "linear-gradient(135deg, #F4F1EA 0%, #ECE7DC 100%)" }}
              >
                {combo.thumbnail ? (
                  <img
                    src={combo.thumbnail}
                    alt={combo.name}
                    className="h-full w-full object-cover object-top"
                  />
                ) : (
                  combo.items.slice(0, 3).map((item, i) => (
                    <div
                      key={i}
                      className="flex h-14 w-14 items-center justify-center rounded-[10px] text-[11px] font-bold"
                      style={{
                        background: "rgba(255,255,255,0.6)",
                        color: "var(--chutney-green)",
                      }}
                    >
                      {item.productSku.slice(0, 3).toUpperCase()}
                    </div>
                  ))
                )}
              </div>

              {/* Info */}
              <h3
                className="font-bold leading-snug"
                style={{ fontSize: 16, letterSpacing: "-0.3px", color: "var(--techno-black)" }}
              >
                {combo.name}
              </h3>
              {combo.description && (
                <p
                  className="flex-1 leading-snug"
                  style={{ fontSize: "12.5px", color: "var(--techno-black-50)", lineHeight: 1.5 }}
                >
                  {combo.description}
                </p>
              )}

              {/* Footer */}
              <div
                className="flex items-center justify-between gap-2.5 pt-3.5"
                style={{ borderTop: "1px dashed var(--border)" }}
              >
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-bold" style={{ color: "var(--techno-black)" }}>
                      ₹{combo.sellingPrice}
                    </span>
                    {combo.mrp > combo.sellingPrice && (
                      <span
                        className="font-medium"
                        style={{ fontSize: 12, textDecoration: "line-through", color: "var(--techno-black-50)" }}
                      >
                        ₹{combo.mrp}
                      </span>
                    )}
                  </div>
                  {combo.savings > 0 && (
                    <p
                      className="mt-0.5 text-[11px] font-bold"
                      style={{ color: "var(--chutney-green)" }}
                    >
                      Save ₹{combo.savings}
                    </p>
                  )}
                </div>

                <button
                  onClick={() => handleAdd(combo)}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-4 py-2.5 text-[13px] font-semibold text-white transition active:scale-95"
                  style={{
                    background: isAdded ? "#C84535" : "#E4604E",
                  }}
                >
                  {isAdded ? "Added ✓" : "Add bundle"}
                  {!isAdded && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M5 12h14M13 5l7 7-7 7" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {combos.length > 3 && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setShowAll((v) => !v)}
            className="flex items-center gap-2 rounded-xl border px-6 py-2.5 text-sm font-semibold transition hover:shadow-sm"
            style={{
              borderColor: "var(--border)",
              color: "var(--techno-black-70)",
              background: "var(--surface)",
            }}
          >
            {showAll ? (
              <>
                Show less
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="18 15 12 9 6 15"/>
                </svg>
              </>
            ) : (
              <>
                View all {combos.length} bundles
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </>
            )}
          </button>
        </div>
      )}
    </section>
  );
};
